import React from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useTranslations } from '@libs/translations';
import { Points } from '@styles';
import { useEditorIframe } from '@libs';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { CategoryNavigation } from '@components/Category-Navigation';
import { Container, PageTop, Pagination } from '@components/Help-Center-Components';

import { HELP_CENTER, HELP_ARTICLES_PER_PAGE } from '@constants';
import { ArticleCard } from '@components/Help-Center-Components/article-card';

export const ArticleSection = ({ data }) => {
  const { locale } = useSelector((state) => state.settings);
  const translations = useTranslations();
  const isOpenInEditorIframe = useEditorIframe();
  const links =
    data.currentCategory === `all`
      ? [
          {
            href: `/${HELP_CENTER.MAIN}`,
            name: translations.t('site.help.title'),
          },
          {
            href: '',
            name: `${data.caption}`,
          },
        ]
      : [
          {
            href: `/${HELP_CENTER.MAIN}`,
            name: translations.t('site.help.title'),
          },
          {
            href: `/${HELP_CENTER.MAIN}/${data.slug}`,
            name: `${data.caption}`,
          },
          {
            href: '',
            name: data.categories?.find((item) => item.slug === data.currentCategory)?.caption,
          },
        ];

  const categories = data.categories && [
    { id: 0, caption: translations.t(`site.help.${data.slug}.all_content`), slug: 'all', isHidden: false },
    ...data.categories,
  ];
  const paginationStartUrl =
    data.currentCategory === `all`
      ? `/${HELP_CENTER.MAIN}/${data.slug}`
      : `/${HELP_CENTER.MAIN}/${data.slug}/${data.currentCategory}`;

  return (
    <Wrapper>
      <BreadcrumbsWrapper>
        <Breadcrumbs links={links} isOpenInEditorIframe={isOpenInEditorIframe} />
      </BreadcrumbsWrapper>
      <Container>
        <PageTop title={data.caption} description={data.description} />
        <CategoryNavigationWrap>
          <CategoryNavigation
            categories={categories?.filter((item) => !item.isHidden)}
            currentCategory={data.currentCategory}
            startUrl={`/${HELP_CENTER.MAIN}/${data.slug}`}
            isOpenInEditorIframe={isOpenInEditorIframe}
          />
        </CategoryNavigationWrap>
        <CardsWrap>
          {data.items?.map((item) => (
            <ArticleCard locale={locale} key={item.id} {...item} sectionUrl={data.slug} />
          ))}
        </CardsWrap>
        {data.total > HELP_ARTICLES_PER_PAGE && (
          <Pagination
            currentPage={data.currentPage}
            totalPages={Math.ceil(data.total / HELP_ARTICLES_PER_PAGE)}
            locale={locale}
            startUrl={paginationStartUrl}
          />
        )}
      </Container>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  margin: 55px 16px 0;
  width: calc(100vw - 32px);
  overflow: auto;

  @media ${Points.tablet_landscape} {
    margin: 30px 28px 0;
    width: calc(100% - 46px);
  }

  @media ${Points.laptop_wide} {
    margin: 40px auto;
    width: 1280px;
  }

  @media ${Points.fullhd} {
    margin: 40px auto;
    width: 1386px;
  }
`;

const BreadcrumbsWrapper = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  margin-bottom: 16px;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: 768px) {
    overflow: visible;
  }
`;

const CategoryNavigationWrap = styled.div`
  margin: 70px 0 30px;

  @media ${Points.laptop_wide} {
    margin: 80px 0 40px;
  }

  @media ${Points.fullhd} {
    margin: 90px 0 50px;
  }
`;

const CardsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 340px;
  margin: 0 auto;

  @media ${Points.tablet_portrait} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    width: 695px;
  }

  @media ${Points.tablet_landscape} {
    grid-template-columns: repeat(3, 1fr);
    width: 966px;
  }

  @media ${Points.laptop_wide} {
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
    width: 1280px;
  }

  @media ${Points.fullhd} {
    width: 1380px;
  }
`;
